/**
 * The dashboard's way into the race replays (useRaceReplay.ts): each series' latest race, played
 * again on the live race page with the player, the track map, strategy and race control. For each
 * series (WRL, IMSA, SRO…), its latest weekend's longest race the viewer can open, the most recent
 * series first; hidden when there is none. Every race page has its own ▶ Replay as well.
 */
import { Link } from "react-router-dom";
import type { LatestEvent, LatestRace } from "../dashboard/types";
import { RACE_REPLAY_PREFIX } from "./useRaceReplay";

/** Most series offered on the card. */
const MAX_SERIES = 4;

export interface ReplayPick {
  event: LatestEvent;
  race: LatestRace;
}

/** The races to offer: each series' latest weekend's longest race the viewer can open, latest series first. */
export function replayPicks(events: LatestEvent[] | null): ReplayPick[] {
  const out: ReplayPick[] = [];
  const seen = new Set<string>();
  for (const event of events ?? []) {
    if (seen.has(event.series)) continue;
    const open = event.races.filter((r) => !r.locked);
    if (!open.length) continue;
    seen.add(event.series);
    out.push({ event, race: [...open].sort((a, b) => (b.maxLap ?? 0) - (a.maxLap ?? 0))[0] });
    if (out.length >= MAX_SERIES) break;
  }
  return out;
}

/** "Sebring International Raceway" → "Sebring", for a short link. */
const shortTrack = (t: string) => t.replace(/\s+(International\s+)?(Raceway|Motorsports Park|Motor Speedway( RC)?|Speedway|Circuit|Park|Campus)$/i, "").trim() || t;

export function RaceReplayCard({ events }: { events: LatestEvent[] | null }) {
  const picks = replayPicks(events);
  if (!picks.length) return null;
  return (
    <section aria-label="Race replays" className="flex flex-col gap-2 rounded-xl border border-[var(--rt-border)] bg-[var(--rt-surface)] px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--rt-accent)]">Race replays</span>
        <span className="text-xs text-[var(--rt-text-muted)]">Watch a race again as it ran: track map, strategy, pit stops and race control, at up to 3×.</span>
      </div>
      <ul className="m-0 flex list-none flex-row flex-wrap gap-1.5 p-0">
        {picks.map(({ event, race }) => (
          <li key={race.id}>
            <Link
              to={`/live/${RACE_REPLAY_PREFIX}${race.id}`}
              title={`Play ${event.seriesLabel ?? event.series} at ${event.track} again: pause, rewind, 2× and 3×.`}
              className="flex min-h-[40px] items-center gap-2 rounded-lg border border-[var(--rt-border)] px-3 text-[14px] font-semibold hover:border-[var(--rt-accent)] hover:text-[var(--rt-accent)] sm:min-h-[36px]"
            >
              <span aria-hidden className="inline-block h-0 w-0 shrink-0 border-y-[5px] border-l-[8px] border-y-transparent border-l-[var(--rt-accent)]" />
              <span className="truncate">
                {event.series} · {shortTrack(event.track)} · {race.session || race.name}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
